import { z } from 'zod';

// «Расчёт дня» на движке — MVP C4 (specs/002-rules-engine/mvp.md, часть C; data-model.md А.8, Б.6). Часть ответа
// `GET …/cells/:personId/:date/calc` при табеле на движке (`TIMESHEET_ON_ENGINE`): итог дня из `day_results` и трасса
// шагов в публичном виде (`TraceRowPublic`, А.8) — без «было», «стало» и цели операции, фраза — по шаблону причины.
// Строки трассы периода (R2 «по итогу периода», I1, R5) — из `period_results`, только за эту дату:
// `reasonParams.дата` — эта дата, `reasonParams.период` — период, в который она входит.

/** Состояния ячейки движка — состояния 001 (`DayResult.state`, А.8). */
export const ENGINE_DAY_STATES = ['ok', 'нет_выхода', 'нет_входа', 'на_объекте', 'пусто'] as const;
export const EngineDayState = z.enum(ENGINE_DAY_STATES);
export type EngineDayState = z.infer<typeof EngineDayState>;

/** Шаг строки трассы: кубики и часть (у составного шага — несколько кубиков). Версия реализации — `impl` строки. */
export const TraceStep = z.object({
  cubes: z.array(z.string()),
  part: z.string().optional(),
});
export type TraceStep = z.infer<typeof TraceStep>;

/** Источник параметров шага: уровень (закон, объект, шаблон), версия набора, объект, фаза. */
export const TraceProvenance = z.object({
  level: z.string(),
  versionId: z.number().nullable(),
  objectId: z.string(),
  phase: z.string(),
});
export type TraceProvenance = z.infer<typeof TraceProvenance>;

/**
 * Строка трассы для показа (`TraceRowPublic`, А.8). `phrase` — шаблон причины с прочитанными параметрами набора и
 * параметрами причины; значение с признаком `personalData` — `{ hidden: true }`, во фразе — подпись поля в фигурных
 * скобках.
 */
export const TraceRowPublic = z.object({
  seq: z.number().int(),
  /** Сколько подряд идущих однотипных строк шага (та же операция и причина) свёрнуто в эту; номер — первой. */
  count: z.number().int().min(1),
  step: TraceStep,
  impl: z.number().int(),
  op: z.string(),
  /** Вид цели операции (проход, отрезок, смена, день, срез, период); `null` — операция без цели. */
  targetKind: z.string().nullable(),
  deltaMs: z.number(),
  reasonCode: z.string().nullable(),
  phrase: z.string(),
  params: z.record(z.string(), z.unknown()),
  reasonParams: z.record(z.string(), z.unknown()),
  provenance: TraceProvenance.nullable(),
});
export type TraceRowPublic = z.infer<typeof TraceRowPublic>;

/** Что показать о шаге: название кубиков по каталогу и подписи прочитанных параметров (`ParamMeta.label`). */
export const TraceStepInfo = z.object({
  title: z.string(),
  labels: z.record(z.string(), z.string()),
});
export type TraceStepInfo = z.infer<typeof TraceStepInfo>;

/** Ключ шага в словаре `steps`: `G5@1`, `B1+B3+B4+B7@1`, `R2·по_дню@1`. */
export function traceStepKey(step: TraceStep, impl: number): string {
  return `${step.cubes.join('+')}${step.part ? `·${step.part}` : ''}@${impl}`;
}

/** Строка дня: строка Табеля или вне Табеля; `value` — `roundedMs ?? ms` в формате R5 объекта, без R5 — `null`. */
export const EngineDayLine = z.object({
  row: z.string(),
  ms: z.number(),
  roundedMs: z.number().nullable(),
  value: z.string().nullable(),
  inWorked: z.boolean(),
  overlay: z.boolean(),
});
export type EngineDayLine = z.infer<typeof EngineDayLine>;

/** Ошибка расчёта (А.5): код и шаг; `step: null` — проверка ядра или вход, а не шаг. */
export const EngineCalcError = z.object({
  code: z.string(),
  step: TraceStep.extend({ impl: z.number().int() }).nullable(),
});
export type EngineCalcError = z.infer<typeof EngineCalcError>;

/**
 * Строка чека расчёта дня (MVP C4, макет «чек и полоса проходов» 12.10.2026): подпись, пояснение простым языком и
 * значение справа. Виды: `присутствие` (на объекте; «засчитано» при потерянном проходе), `обед`, `остаётся` (после
 * вычета обеда), `округление` (или минимум за короткий визит), `ночь` (справочно: из них ночью), `выходной`,
 * `документ`, `состояние` (нет выхода, нет входа, на объекте). Значения правил (порог обеда, норма, шаг) — из
 * параметров строк трассы. `tone`: `warn` — проверить (потерян проход), `bad` — проблема (нет выхода, нет входа),
 * `muted` — справочная строка; нет — обычная.
 */
export const DAY_RECEIPT_KINDS = [
  'присутствие',
  'обед',
  'остаётся',
  'округление',
  'ночь',
  'выходной',
  'документ',
  'состояние',
] as const;
export const DayReceiptRow = z.object({
  kind: z.enum(DAY_RECEIPT_KINDS),
  label: z.string(),
  note: z.string(),
  /** Значение справа: «3 ч 15 мин», «− 1 ч», «9» (как в ячейке, R5); нечего показать — «—». */
  value: z.string(),
  tone: z.enum(['warn', 'bad', 'muted']).optional(),
  /** Правило экрана «Правила расчёта» (`RuleGroup.id`), по которому посчитана строка: строка — ссылка на правило (004 FR-007). */
  ruleGroup: z.string().optional(),
  /** Вопрос этого правила (`ANSWERS[ruleGroup].question`) — подпись ссылки, тот же текст, что на странице правил (SC-015). */
  ruleQuestion: z.string().optional(),
});
export type DayReceiptRow = z.infer<typeof DayReceiptRow>;

/**
 * Итог чека «В табеле» по расчёту: код строки Т-13 и название («Я» — «явка»; документ — его код), откуда значение
 * («по проходам СКУД, правок нет», «документ на весь день») и значение как в ячейке (R5; документ — код). Правку и
 * код отсутствия из табеля интерфейс показывает поверх (их знает ячейка, а не расчёт). Часов нет — `code` и `value`
 * пустые.
 */
export const DayReceiptTotal = z.object({
  code: z.string().nullable(),
  name: z.string().nullable(),
  note: z.string(),
  value: z.string().nullable(),
});
export type DayReceiptTotal = z.infer<typeof DayReceiptTotal>;

/** Чек расчёта дня: строки сверху вниз и итог «В табеле». Ошибка расчёта — строк нет. */
export const DayReceipt = z.object({
  rows: z.array(DayReceiptRow),
  total: DayReceiptTotal,
});
export type DayReceipt = z.infer<typeof DayReceipt>;

/** Отметка прохода на полосе дня: время `чч:мм` (в другую дату — `дд.мм чч:мм`) и терминал (для подсказки). */
export const DayMark = z.object({
  time: z.string(),
  device: z.string().nullable(),
});
export type DayMark = z.infer<typeof DayMark>;

/**
 * Отрезок полосы «Проходы за день» — по учтённым проходам смен даты (лишние отметки N3, N4 не входят), по порядку:
 * - `пара` — вход и выход, номер `n` по порядку (1, 2, 3…);
 * - `потерян_вход` — выход после выхода: область между ними, входа нет; `потерян_выход` — вход после входа;
 * - `нет_выхода` (A1), `на_объекте` (A5) — вход без выхода, отрезок открыт вправо (`to` пусто); `нет_входа` (A2) —
 *   выход без входа, открыт влево (`from` пусто).
 * `from`, `to` — секунды от 00:00 даты по местному времени объекта (смена через полночь — больше 86 400).
 * `duration` — время на объекте (только у пары и «на объекте»), `away` — затем вне объекта до следующего входа,
 * `awayCounted` — это время засчитано (лежит внутри «от первого входа до последнего выхода»). Длительности —
 * `ч мм мин`, без секунд.
 */
export const DAY_SEGMENT_KINDS = [
  'пара',
  'потерян_вход',
  'потерян_выход',
  'нет_выхода',
  'нет_входа',
  'на_объекте',
] as const;
export const DaySegment = z.object({
  kind: z.enum(DAY_SEGMENT_KINDS),
  /** Номер пары; у потерянного прохода, нет выхода и нет входа — `null` (в таблице — «?»). */
  n: z.number().int().nullable(),
  in: DayMark.nullable(),
  out: DayMark.nullable(),
  from: z.number().nullable(),
  to: z.number().nullable(),
  duration: z.string().nullable(),
  away: z.string().nullable(),
  awayCounted: z.boolean(),
});
export type DaySegment = z.infer<typeof DaySegment>;

/** Засчитано «от первого входа до последнего выхода» (потерян проход или метод `first_last` с несколькими парами). */
export const DayCountedSpan = z.object({
  from: z.number(),
  to: z.number(),
  duration: z.string(),
});
export type DayCountedSpan = z.infer<typeof DayCountedSpan>;

/**
 * «Проходы за день»: отрезки по порядку, засчитанные промежутки «от первого входа до последнего выхода», убранные
 * лишние отметки турникета («08:52:33 — повтор выхода через 3 с»), итоги (на объекте по отметкам, вне объекта,
 * засчитано) и предупреждения о потерянном проходе для шапки окна («Потерян вход между выходами 12:30 и 16:10 —
 * проверьте день.»). Обеда здесь нет: он только в чеке.
 */
export const DayPasses = z.object({
  segments: z.array(DaySegment),
  counted: z.array(DayCountedSpan),
  extra: z.array(z.string()),
  totals: z.object({
    present: z.string().nullable(),
    away: z.string().nullable(),
    counted: z.string().nullable(),
  }),
  lost: z.array(z.string()),
});
export type DayPasses = z.infer<typeof DayPasses>;

/** Результат дня есть. */
export const EngineDayCalculated = z.object({
  calculated: z.literal(true),
  state: EngineDayState,
  /** «Предварительно»: смена дня ждёт выхода (A5); `computedAt` — момент расчёта. */
  provisional: z.boolean(),
  computedAt: z.string().nullable(),
  /** Строки Табеля (в том числе наложения). */
  lines: z.array(EngineDayLine),
  /** Строки вне Табеля: кандидаты, «вне графика», «не засчитано». */
  offTabel: z.array(EngineDayLine),
  /** Ошибка расчёта дня: ячейка пустая (FR-055). */
  error: EngineCalcError.nullable(),
  /** Ошибка расчёта периода (месяца даты): строк трассы периода нет. */
  periodError: EngineCalcError.nullable(),
  engineVersion: z.string(),
  /** Версия правил объекта (номер версии файла правил); пусто — набора у дня нет (ошибка входа). */
  rulesetVersionId: z.number().nullable(),
  implMap: z.record(z.string(), z.number()),
  /**
   * Короткое объяснение дня простым языком (ASD-STE100): 3–6 пунктов — приход и уход, присутствие, обед, выходной
   * или документ, округление, итог «В табеле». Собирает сервер из трассы этой даты.
   */
  summary: z.array(z.string()),
  /** Чек расчёта дня — строки и итог «В табеле» (окно показывает его вместо `summary`). */
  receipt: DayReceipt,
  /** Полоса и таблица «Проходы за день». */
  day: DayPasses,
  /**
   * Трасса дня для разработчика: только эта дата (смены этой даты и строки с датой), только строки с изменениями
   * (без `noop`; `noop` с решением шага остаётся рядом с его изменениями), повторы свёрнуты (`count`).
   */
  trace: z.array(TraceRowPublic),
  /** Строки трассы периода за эту дату. */
  periodTrace: z.array(TraceRowPublic),
  /** Шаги трассы и ошибки: ключ — `traceStepKey`. */
  steps: z.record(z.string(), TraceStepInfo),
});
export type EngineDayCalculated = z.infer<typeof EngineDayCalculated>;

/** «Расчёт дня» на движке: результата дня ещё нет (пересчёт не дошёл) или он есть. */
export const EngineDayCalc = z.discriminatedUnion('calculated', [
  z.object({ calculated: z.literal(false) }),
  EngineDayCalculated,
]);
export type EngineDayCalc = z.infer<typeof EngineDayCalc>;
